import { DOCUMENT, NgClass, NgStyle, SlicePipe } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  inject,
  Injector,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  Renderer2,
} from '@angular/core';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';
import { AppPopoverWrapperComponent } from '../../popover/popover-wrapper/popover-wrapper.component';
import { BuilderService, SubscribeNewsletterModalService } from '@scalefast/ecommerce-core';
import { BuilderContent } from '@builder.io/sdk-angular';

@Component({
  imports: [NgStyle, SlicePipe, NgClass, AppPopoverWrapperComponent],
  selector: 'app-advantages-banner',
  standalone: true,
  styleUrls: ['./advantages-banner.styles.scss'],
  templateUrl: './advantages-banner.component.html',
})
export class AppAdvantagesBannerComponent implements OnDestroy, OnChanges {
  private injector = inject(Injector);
  @ViewChild('advSlider') advSlider!: ElementRef<HTMLElement>;
  cards: any[] = [];
  finalCards: any;
  slider!: KeenSliderInstance;
  isSliderActive: boolean = false;
  slidesPerView: number = 4;
  visibleSlides: number[] = [];
  currentSlide: number = 0;
  readonly slideChangeRate = 3000;

  constructor(
    private builderService: BuilderService,
    private changeDetectorRef: ChangeDetectorRef,
    private subscribeNewsletterModalService: SubscribeNewsletterModalService,
    private el: ElementRef,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _doc: Document,
  ) {
    this.builderService.getContentByModelName('advantages-banner').subscribe((builderContent: BuilderContent) => {
      if (!builderContent.data) {
        return;
      }
      this.cards = builderContent.data.cards;
      this.createSlider();
      if (this.cards) {
        let headerSite = this._doc.getElementById('header__site');
        if (headerSite) {
          headerSite?.classList.add('advantages-banner');
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.slider?.update(); //HOTFIX: This may prevent valid updates on the slider, needs a better a solution and/or testing
  }

  createSlider(): void {
    afterNextRender(
      () => {
        if (this.cards && this.cards.length > 0) {
          this.slider = new KeenSlider(
            this.advSlider.nativeElement,
            {
              initial: this.currentSlide,
              renderMode: 'performance',
              slides: {
                origin: 'auto',
                perView: () => {
                  this.slidesPerView = 4;
                  this.updateVisibleSlides();
                  return 4;
                },
                spacing: 10,
              },
              breakpoints: {
                '(max-width: 992px)': {
                  loop: true,
                  slides: {
                    perView: () => {
                      this.slidesPerView = 2;
                      this.updateVisibleSlides();
                      return 2;
                    },
                    origin: 'auto',
                  },
                },
                '(max-width: 768px)': {
                  slides: {
                    perView: () => {
                      this.slidesPerView = 1;
                      this.updateVisibleSlides();
                      return 1;
                    },
                    origin: 'center',
                  },
                },
              },
              slideChanged: (s) => {
                this.currentSlide = s.track.details.rel;
                this.updateVisibleSlides();
              },
            },
            [
              (slider: any) => {
                let timeout: any;
                let mouseOver = false;
                const clearNextTimeout = () => {
                  clearTimeout(timeout);
                };
                const nextTimeout = () => {
                  clearTimeout(timeout);
                  if (mouseOver) return;
                  timeout = setTimeout(() => {
                    slider.next();
                  }, this.slideChangeRate);
                };
                slider.on('created', () => {
                  slider.container.addEventListener('mouseover', () => {
                    mouseOver = true;
                    clearNextTimeout();
                  });
                  slider.container.addEventListener('mouseout', () => {
                    mouseOver = false;
                    nextTimeout();
                  });
                  nextTimeout();
                });
                slider.on('dragStarted', clearNextTimeout);
                slider.on('animationEnded', nextTimeout);
                slider.on('updated', nextTimeout);
              },
            ],
          );
        }
        this.isSliderActive = true;
        this.changeDetectorRef.detectChanges();
      },
      { injector: this.injector },
    );
  }

  getVisibleSlides() {
    let visibleSlides: number[] = [];
    // Round slidesPerView to the closest number
    const slidesPerView = Math.round(this.slidesPerView);

    if (this.cards) {
      for (let i = 0; i < slidesPerView; i++) {
        let index = (this.currentSlide + i) % this.cards.length; // Infinite loop
        visibleSlides.push(index);
      }
    }
    return visibleSlides;
  }

  updateVisibleSlides() {
    this.visibleSlides = this.getVisibleSlides();
  }

  isVisibleSlide(index: number): boolean {
    return this.visibleSlides.includes(index);
  }

  clickOpenNewsletterModal($event: any): void {
    $event.preventDefault();
    this.subscribeNewsletterModalService.showModal();
  }

  ngOnDestroy() {
    if (this.slider) this.slider.destroy();
  }
}
